/**
 * High5 · Haftalık Net Satış — GİRİŞ GEREKTİRMEYEN paylaşım sayfası.
 *
 * platform.glowytics.com/p/<gizli-slug> — sidebar YOK, marka seçici YOK,
 * üstte sadece Glowytics logosu. İçerik /ads-platforms/unified-campaigns
 * sayfasındaki iki kartın AYNISI (aynı bileşenler import ediliyor, kopya değil):
 *   1. MARKA BAZLI HAFTALIK HARCAMA · GERÇEKLEŞEN NET SATIŞ · ROAS
 *   2. NET SATIŞ GRAFİKLERİ — MARKA MARKA (trend + pay)
 *
 * Veri tek uçtan gelir: GET /api/public-reports/<gizli-slug> (auth yok, 15 dk
 * sunucu cache'i). Slug config/publicReportLinks.js'ten okunur — backend'deki
 * HIGH5_NET_SALES_SLUG ile aynı olmak ZORUNDA. Marka kovalama (matchBrand)
 * burada da giriş yapılmış sayfadaki ile aynı fonksiyonla yapılır → iki sayfa
 * asla ayrışmaz.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import {
  BrandWeeklyNetSalesMatrixCard,
  BrandWeeklyNetSalesChartsCard,
  bucketWeeklyRowsByBrand,
} from '../ads-platforms/UnifiedCampaigns';
import { getBrandGroups, HIGH5_BRAND_ID } from '../../config/brandGroups';
import { HIGH5_NET_SALES_SLUG } from '../../config/publicReportLinks';

const API_BASE = 'https://api.glowytics.com';

export default function High5NetSalesPublic() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const BRAND_GROUPS = useMemo(() => getBrandGroups(HIGH5_BRAND_ID), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // credentials YOK: uç zaten public, çerez göndermek CORS'u gereksiz sıkar.
      const r = await fetch(`${API_BASE}/api/public-reports/${HIGH5_NET_SALES_SLUG}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setPayload(await r.json());
    } catch (e) {
      setError(e.message || 'Veri alınamadı');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Gizli adresin gizli kalmasının tek yolu link disiplini değil: sayfa bir kez
  // indekslenirse arama sonucundan bulunur. noindex + referrer kapalı → linke
  // tıklanan dış siteye adres sızmaz. Sayfadan çıkınca etiketler geri alınır.
  useEffect(() => {
    document.title = 'High5 · Haftalık Net Satış';
    const tags = [
      { name: 'robots', content: 'noindex, nofollow, noarchive' },
      { name: 'referrer', content: 'no-referrer' },
    ].map(({ name, content }) => {
      const el = document.createElement('meta');
      el.setAttribute('name', name);
      el.setAttribute('content', content);
      document.head.appendChild(el);
      return el;
    });
    return () => tags.forEach(el => el.remove());
  }, []);

  const weeks = useMemo(
    () => bucketWeeklyRowsByBrand(payload?.spend?.rows, BRAND_GROUPS),
    [payload, BRAND_GROUPS]
  );
  const netSales = useMemo(
    () => ({ rows: payload?.netSales?.rows || [] }),
    [payload]
  );

  const generatedAt = payload?.generatedAt
    ? new Date(payload.generatedAt).toLocaleString('tr-TR', {
        day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
      })
    : null;

  // Google/Meta tarafı düşerse matris yarım dolar. Sessizce 0 göstermek yerine
  // uyarı bas — "harcama düşmüş" diye okunmasın.
  const upstreamErrors = [
    payload?.errors?.google && `Google Ads: ${payload.errors.google}`,
    payload?.errors?.meta && `Meta: ${payload.errors.meta}`,
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-3">
          {/* Solda Glowytics'in AÇIK (yazılı) logosu — sidebar'daki `logoicon.png`
              sadece G amblemi, paylaşım sayfasında marka okunur olmalı. */}
          <img src="/Calisma-Yuzeyi-20@5x-4.png" alt="Glowytics" className="h-7 w-auto" />
          <div className="flex items-center gap-4 text-xs text-gray-400">
            {generatedAt && <span className="hidden sm:inline">Son güncelleme: {generatedAt}</span>}
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              title="Sayfayı yeniden yükle"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {/* Raporun kime ait olduğu sağda: siyah high5 logosu, araya ince ayraç */}
            <span className="w-px h-6 bg-gray-200" aria-hidden="true" />
            <img src="/high5logo.svg" alt="high5" className="h-6 w-auto" />
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {error && (
          <div className="flex items-start gap-3 p-4 rounded-2xl border border-red-200 bg-red-50 text-sm text-red-700">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">Rapor yüklenemedi</div>
              <div className="text-red-600 mt-0.5">{error}</div>
            </div>
          </div>
        )}

        {!error && upstreamErrors.length > 0 && (
          <div className="flex items-start gap-3 p-4 rounded-2xl border border-amber-200 bg-amber-50 text-sm text-amber-800">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">Harcama verisi eksik olabilir</div>
              <ul className="mt-0.5 list-disc list-inside text-amber-700">
                {upstreamErrors.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          </div>
        )}

        {/* Paylaşım sayfası 8 haftayla açılır (panelde matris 6h ile açılıyor);
            ziyaretçi kartın kendi 4h/6h/8h/12h butonlarından değiştirebilir. */}
        <BrandWeeklyNetSalesMatrixCard
          weeks={weeks}
          netSales={netSales}
          loading={loading}
          brandGroups={BRAND_GROUPS}
          brandId={HIGH5_BRAND_ID}
          defaultWeekCount={8}
          mobileScroll
        />

        <BrandWeeklyNetSalesChartsCard
          weeks={weeks}
          netSales={netSales}
          loading={loading}
          brandGroups={BRAND_GROUPS}
          brandId={HIGH5_BRAND_ID}
          defaultWeekCount={8}
          mobileScroll
        />

        <div className="text-center text-xs text-gray-400 pb-6">
          Glowytics · Harcama live Google Ads + Meta, net satış ERP (KDV hariç, iadeler düşülmüş)
        </div>
      </main>
    </div>
  );
}
